import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { SwiperOptions } from 'swiper/types';

import { ENVIRONMENT, Environment, NewFlowDateFormatOptions, NewFlowDateSelector } from '@environment';
import { rt } from '@usheru-hff/sdk/utils';
import { isTodayOrTomorrowForTimeZone } from '@usheru-hff/shared/utils';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

const FALLBACK_SHOWTIMES_TIME_ZONE = 'UTC';

@Component({
  selector: 'ush-showtime-dates',
  templateUrl: './ush-showtime-dates.component.html',
  styleUrls: ['./ush-showtime-dates.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false
})
export class UshShowtimeDatesComponent implements OnInit, OnChanges {
  @Input() dates: { [key: string]: string } = null;
  @Output() dateSelected = new EventEmitter<string>();
  @Output() dateInitialized = new EventEmitter<string>();

  @ViewChild('swiper') swiper: any;

  prevArrowSelector: string;
  nextArrowSelector: string;
  prevIcon = faAngleLeft;
  nextIcon = faAngleRight;
  nextArrowDisabled = false;
  prevArrowDisabled = false;

  swiperConfig: SwiperOptions;
  clickedItem: number = null;
  datesUI: { [key: string]: string } = null;
  dateSelectorCustomConfig: NewFlowDateSelector = null;
  private readonly timeZone: string;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(ENVIRONMENT) private env: Environment,
    private translationService: TranslationsService
  ) {
    this.timeZone = this.resolveTimeZone();
    this.dateSelectorCustomConfig = this.env?.showtimeStreamingConfig?.newFlowAttributes?.dateSelector || null;
  }

  resetComponent(data: { [key: string]: string }): void {
    this.formatDates(data);
    this.setInitialDate(data);
  }

  ngOnInit(): void {
    this.setSwiperConfig();
    if (this.dates && !this.datesUI) {
      this.resetComponent(this.dates);
    }
  }

  findKeyWithLowestDate(dateMap: { [key: string]: string }): string {
    let lowestKey = null;
    for (const key in dateMap) {
      if (!lowestKey || key < lowestKey) {
        lowestKey = key;
      }
    }
    return lowestKey;
  }

  formatDates(dateMap: { [key: string]: string } = null): void {
    if (dateMap) {
      this.datesUI = null;
      this.datesUI = { ...dateMap };
      for (const dateKey in this.datesUI) {
        if (Object.prototype.hasOwnProperty.call(this.datesUI, dateKey)) {
          const todayOrTomorrow = isTodayOrTomorrowForTimeZone(dateKey, this.timeZone);
          if (
            (todayOrTomorrow === 'today' || todayOrTomorrow === 'tomorrow') &&
            !this.dateSelectorCustomConfig?.breakDateInNewLines
          ) {
            this.datesUI[dateKey] = todayOrTomorrow;
          }
        }
      }
    }
  }

  private setInitialDate(dateMap: { [key: string]: string }): void {
    const initialDate = this.findKeyWithLowestDate(dateMap);
    if (!initialDate) {
      this.clickedItem = null;
      return;
    }

    this.clickedItem = 0;
    this.dateInitialized.emit(initialDate);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.dates?.currentValue && changes?.dates?.previousValue !== changes?.dates?.currentValue) {
      this.resetComponent(changes.dates.currentValue);
      const swiper = this.getSwiperInstance();
      if (swiper) {
        swiper.slideTo(0);
      }
    }
  }

  slidePrev() {
    const swiperInstance = this.getSwiperInstance();
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  }

  slideNext() {
    const swiperInstance = this.getSwiperInstance();
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  }

  private getSwiperInstance() {
    return this.swiper?.nativeElement?.swiper || null;
  }

  private resolveTimeZone(): string {
    if (!isPlatformBrowser(this.platformId)) {
      return FALLBACK_SHOWTIMES_TIME_ZONE;
    }

    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_SHOWTIMES_TIME_ZONE;
  }

  private setSwiperConfig() {
    this.setArrowsCssSelectors();
    this.swiperConfig = {
      slidesPerView: 'auto',
      observer: true,
      observeParents: true,
      modules: [myPlugin],
      freeMode: false,
      spaceBetween: this.dateSelectorCustomConfig?.spaceBetweenDates || 5,
      breakpointsBase: 'container',
      scrollbar: {
        snapOnRelease: true,
        enabled: true,
        draggable: true,
        dragSize: 100
      },
      navigation: true,
      injectStyles: [
        `.swiper-horizontal {
          position: relative;
          overflow: hidden;
        }
        .swiper-button-next.swiper-button-disabled,
        .swiper-button-prev.swiper-button-disabled {
          opacity: 0;
        }
        .swiper-button-next, .swiper-button-prev {
          height: 130%;
          top: 12px;
          width: 80px;
          color: transparent;
          ${this.dateSelectorCustomConfig?.enableBlurOnEdges ? '' : 'display:none;'}
        }
        .swiper-button-next {
          right: 0;
          background: ${
            this.dateSelectorCustomConfig?.enableBlurOnEdges
              ? 'linear-gradient(to left, var(--dates-carousel-background, transparent), transparent);'
              : 'transparent'
          }
        }
        .swiper-button-prev {
          left: 0;
          background: ${
            this.dateSelectorCustomConfig?.enableBlurOnEdges
              ? 'var(--dates-carousel-button-background, linear-gradient(to right, var(--dates-carousel-background, transparent), transparent));'
              : 'transparent'
          }
        }
        `
      ],
      slideToClickedSlide: this.dateSelectorCustomConfig?.animateOnDateSelect || false,
      initialSlide: 0,
      breakpoints: {
        400: {
          spaceBetween: 8
        },
        700: {
          spaceBetween: 12
        },
        1500: {
          spaceBetween: 20
        }
      }
    };
  }

  private setArrowsCssSelectors() {
    const randomId = this.generateRandomId();
    this.prevArrowSelector = `prev-${randomId}`;
    this.nextArrowSelector = `next-${randomId}`;
  }

  private generateRandomId() {
    return Math.random().toString(36).substring(7);
  }

  dateClicked(i: number, dateStr: string): void {
    this.clickedItem = i;
    this.dateSelected.emit(dateStr);
  }

  dateKeyup(event: KeyboardEvent, i: number, dateStr: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.clickedItem = i;

      this.dateSelected.emit(dateStr);
    }
    // arrows left and right foucs next and previous date
    if (event.key === 'ArrowLeft') {
      const preEl = (event.target as HTMLAnchorElement)?.parentElement?.previousElementSibling as HTMLElement;
      if (preEl) {
        (preEl?.children[0] as HTMLElement)?.focus();
      } else {
        const parentList = (event.target as HTMLAnchorElement)?.parentElement?.parentElement as HTMLElement;
        const lastChild = parentList?.children[parentList.children.length - 1] as HTMLElement;
        (lastChild.children[0] as HTMLElement)?.focus();
      }
    }
    if (event.key === 'ArrowRight') {
      const nextEl = (event.target as HTMLAnchorElement)?.parentElement?.nextElementSibling as HTMLElement;
      if (nextEl) {
        (nextEl?.children[0] as HTMLElement)?.focus();
      } else {
        const parentList = (event.target as HTMLAnchorElement)?.parentElement?.parentElement as HTMLElement;
        const firstChild = parentList?.children[0] as HTMLElement;
        (firstChild.children[0] as HTMLElement)?.focus();
      }
    }
  }

  dateValue(index, value): string {
    return value;
  }

  formatDateInLines(dateString: string, dateKey: string): { key: string; value: string } {
    let formattedDate = '';
    let key = '';

    if (this.dateSelectorCustomConfig?.breakDateInNewLines) {
      if (dateKey?.startsWith('9999-01-01')) {
        return {
          key: 'showtimes.day.tbc-in-lines',
          value: $localize`:@@showtimes.day.tbc-in-lines:T-B-C`
            .split('-')
            .map(item => `<span>${item}</span>`)
            .join('<br />')
        };
      }
      if (this.dateSelectorCustomConfig?.dateFormat === NewFlowDateFormatOptions.MONTH_DATE_DAY) {
        formattedDate = dateString
          .split(' ')
          .map(item => `<span>${item.replace(',', '')}</span>`)
          .reverse()
          .join('<br />');
      } else {
        formattedDate = dateString
          .split(' ')
          .map(item => `<span>${item.replace(',', '')}</span>`)
          .join('<br />');
      }
    } else {
      if (dateKey?.startsWith('9999-01-01')) {
        return { key: 'showtimes.day.tbc', value: $localize`:@@showtimes.day.tbc:TBC` };
      }
      if (dateString?.toLowerCase() === 'today') {
        // formattedDate = rt('', 'public.commons.today', null, $localize`:@@public.commons.today:Today`);
        formattedDate = $localize`:@@public.commons.today:Today`;
        key = 'public.commons.today';
      } else if (dateString?.toLowerCase() === 'tomorrow') {
        //     formattedDate = rt('', 'public.commons.tomorrow', null, $localize`:@@public.commons.tomorrow:Tomorrow`);
        formattedDate = $localize`:@@public.commons.tomorrow:Tomorrow`;
        key = 'public.commons.tomorrow';
      } else {
        formattedDate = dateString;
      }
    }
    return { key, value: formattedDate };
  }
}

function myPlugin({ swiper, extendParams, on }) {
  extendParams({
    debugger: true
  });
  on('init', () => {
    if (!swiper.params.debugger) return;
    // disable left arrow
  });
  on('sliderMove', (swiper, e) => {
    if (!swiper.params.debugger) return;
    // if not in the beginning, enable left arrow
  });
  on('reachBeginning', () => {
    if (!swiper.params.debugger) return;
  });
  on('reachEnd', () => {
    if (!swiper.params.debugger) return;
    // disable right arrow
  });
  on('tap', (swiper, e) => {
    if (!swiper.params.debugger) return;
  });
}
